import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useCountUp } from "../../hooks/useCountUp";

const BAND_COLOR = { quiet: "var(--scarlet-pos)", moderate: "var(--status-expiring-dot)", busy: "var(--status-overdue-dot)" };
const RADIUS = 52;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// UX_MOTION.md "Crowd-meter (flagship wow)": number rolls on change, ring/fill tweens to the new
// level, band color cross-fades, a soft pulse of the RING STROKE WIDTH (never a glow) signals a
// live-refresh update. Reduced-motion: number swaps, no ring animation.
// UX_MOTION.md "Crowd-meter (flagship wow)": on load, the ring sweeps from empty to the real
// level over ~1.8s (never instant) — the single highest-visibility animation in the app, so it
// gets its own deliberately slower, more dramatic ease than every other tween. After that first
// sweep, live-refresh updates go back to the fast, subtle 0.32s tween (a *repeated* 2s sweep on
// every poll would feel laggy, not premium). Number rolls on change, band color cross-fades, a
// soft pulse of the RING STROKE WIDTH (never a glow) signals a live-refresh update. Reduced-
// motion: number swaps, no ring animation.
export function OccupancyRing({ count, capacity, band, testId }) {
  const reducedMotion = useReducedMotion();
  const animatedCount = useCountUp(count);
  const ratio = capacity > 0 ? Math.min(1, count / capacity) : 0;
  const [pulse, setPulse] = useState(false);
  const [mounted, setMounted] = useState(false);
  const prevCount = useRef(count);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 1800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (prevCount.current !== count && !reducedMotion) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 500);
      prevCount.current = count;
      return () => clearTimeout(t);
    }
    prevCount.current = count;
  }, [count, reducedMotion]);

  const color = BAND_COLOR[band] || BAND_COLOR.quiet;

  return (
    <div className="flex items-center gap-6" data-testid={testId}>
      <div className="relative w-32 h-32 shrink-0">
        <svg width="128" height="128" viewBox="0 0 128 128" className="-rotate-90">
          <circle cx="64" cy="64" r={RADIUS} fill="none" stroke="var(--scarlet-hairline)" strokeWidth="10" />
          <motion.circle
            cx="64" cy="64" r={RADIUS} fill="none" stroke={color} strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            initial={{ strokeDashoffset: CIRCUMFERENCE, strokeWidth: 10 }}
            animate={{ strokeDashoffset: CIRCUMFERENCE * (1 - ratio), strokeWidth: pulse ? 13 : 10 }}
            transition={
              reducedMotion
                ? { duration: 0 }
                : mounted
                  ? { strokeDashoffset: { duration: 0.32, ease: [0.22, 1, 0.36, 1] }, strokeWidth: { duration: 0.25, ease: "easeInOut" } }
                  : { strokeDashoffset: { duration: 1.8, ease: [0.16, 1, 0.3, 1] }, strokeWidth: { duration: 0.25, ease: "easeInOut" } }
            }
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold tabular-nums-force" style={{ color: "var(--scarlet-text)" }}>{animatedCount}</span>
          <span className="eyebrow">of {capacity}</span>
        </div>
      </div>
      <div>
        <p className="eyebrow mb-1">Live occupancy</p>
        <motion.p key={band} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.22 }} className="text-sm font-semibold capitalize" style={{ color }}>
          {band || "quiet"}
        </motion.p>
        <p className="text-xs mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>Reflects auto-checkout in real time.</p>
      </div>
    </div>
  );
}
